import { type FormikProps, useFormik } from "formik";
import moment from "moment-timezone";
import { Calendar } from "primereact/calendar";
import { Dropdown } from "primereact/dropdown";
import { forwardRef, useImperativeHandle } from "react";
import { useSelector } from "react-redux";
import { DefaultTextFieldComponent } from "@components/DefaultTextInput";
import { ValidatedField } from "@components/ValidatedField";
import {
	OfferFileSelectionField as FileSelectionField,
	type FileV2,
} from "@features/offer-files/offer-file-selection-field";
import { useTranslation } from "@i18n/hooks";
import {
	EditSubscriberConfigFormProps,
	EditSubscriberConfigFormState,
	type SubscriberVisibilityKind,
} from "./edit-subscriber-config-form.types";
import { getVisibilityKinds, isVisibilityKindDisabled } from "./edit-subscriber-config-form.utils";
import { GenericSelectProductFieldDialog } from "../../../../features/products/generic-select-product-field-dialog/generic-select-product-field-dialog.component";
import { SelectProductFieldDialog } from "../../../../features/products/select-product-field-dialog";
import { type Product } from "../../../../features/products/select-product-field-dialog/select-product-field-dialog.interface";
import { selectActiveFeatureToggles } from "../../../../store/slices/FeatureTogglesSlice";

const DATE_TIME_KINDS: SubscriberVisibilityKind[] = ["AFTER_DATE_TIME", "NOT_AFTER_DATE_TIME"];

export const EditSubscriberConfigForm = forwardRef<
	FormikProps<EditSubscriberConfigFormState>,
	EditSubscriberConfigFormProps
>(function EditSubscriberConfigForm(
	{
		initialTitle,
		initialImageUrl,
		initialVisibility,
		initialDateTime,
		initialLicenseProduct,
		initialCourseContentProduct,
		initialCertificationProduct,
		onSubmit,
	},
	ref,
) {
	const { t } = useTranslation("common");
	const activeFeatureToggles = useSelector(selectActiveFeatureToggles);
	const hasSplitCourseCertification = activeFeatureToggles?.includes("splitCourseCertification");

	const visibilityOptions = getVisibilityKinds(initialVisibility).map((value) => ({
		label: t(`offerSubscriptions.visibilityOptions.${value}`),
		value,
		disabled: isVisibilityKindDisabled(value),
	}));

	const formik = useFormik<EditSubscriberConfigFormState>({
		initialValues: {
			title: initialTitle ?? "",
			image: undefined,
			visibility: initialVisibility,
			dateTime: initialDateTime,
			licenseProduct: initialLicenseProduct,
			courseContentProduct: initialCourseContentProduct,
			certificationProduct: initialCertificationProduct,
		},
		onSubmit,
	});

	useImperativeHandle(ref, () => ({ ...formik }));

	const showDatePicker =
		formik.values.visibility !== null && DATE_TIME_KINDS.includes(formik.values.visibility);

	return (
		<form onSubmit={formik.handleSubmit}>
			<ValidatedField<EditSubscriberConfigFormState, string>
				name="title"
				label={t("offerSubscriptions.config.title")}
				component={DefaultTextFieldComponent}
				formikConfig={formik}
			/>
			<ValidatedField<EditSubscriberConfigFormState, FileV2>
				name="image"
				label={t("offerSubscriptions.config.image")}
				component={({ fieldName, fieldValue, updateField, onChange }) => (
					<>
						{initialImageUrl && !fieldValue && (
							<p className="text-sm text-color-secondary mb-1">{initialImageUrl}</p>
						)}
						<FileSelectionField
							name={fieldName}
							selectedFile={fieldValue}
							setSelectedFile={updateField}
							filterByFileTypes={[
								"image/png",
								"image/jpg",
								"image/jpeg",
								"image/webp",
							]}
							onChange={onChange}
							canUploadFiles
							canDeleteFiles={false}
						/>
					</>
				)}
				formikConfig={formik}
			/>
			<ValidatedField<EditSubscriberConfigFormState, SubscriberVisibilityKind | null>
				name="visibility"
				label={t("offerSubscriptions.config.visibility")}
				component={({ fieldName, fieldValue, updateField }) => (
					<Dropdown
						id={fieldName}
						name={fieldName}
						value={fieldValue}
						options={visibilityOptions}
						optionDisabled="disabled"
						onChange={(e) => {
							// PrimeReact's type-ahead search bypasses optionDisabled, and five
							// German labels start with "Nur" — so re-check here.
							if (isVisibilityKindDisabled(e.value)) {
								return;
							}
							updateField(e.value);
							if (!DATE_TIME_KINDS.includes(e.value)) {
								formik.setFieldValue("dateTime", null);
							}
						}}
						className="w-full"
						placeholder={t("offerSubscriptions.config.visibilityPlaceholder")}
					/>
				)}
				formikConfig={formik}
			/>
			{showDatePicker && (
				<ValidatedField<EditSubscriberConfigFormState, string | null>
					name="dateTime"
					label={t("offerSubscriptions.config.visibilityDateTime")}
					component={({ fieldName, fieldValue, updateField }) => (
						<Calendar
							name={fieldName}
							showTime
							hourFormat="24"
							dateFormat="dd.mm.yy"
							value={
								fieldValue
									? moment(fieldValue.replace(/\[.*?\]/, "")).toDate()
									: undefined
							}
							onChange={(e) =>
								updateField(
									e.value
										? moment(e.value as Date).format("YYYY-MM-DDTHH:mm:ssZ")
										: null,
								)
							}
						/>
					)}
					formikConfig={formik}
				/>
			)}
			<ValidatedField<EditSubscriberConfigFormState, Product>
				name="licenseProduct"
				label={t("offerSubscriptions.config.licenseProduct")}
				component={SelectProductFieldDialog}
				formikConfig={formik}
			/>
			{hasSplitCourseCertification && (
				<>
					<ValidatedField<EditSubscriberConfigFormState, Product>
						name="courseContentProduct"
						label={t("offerSubscriptions.config.courseContentProduct")}
						component={(props) => (
							<GenericSelectProductFieldDialog
								{...props}
								productType={{
									emptyText: t("educationalOffer.noCourseContentProductSelected"),
									buttonLabel: t("educationalOffer.selectCourseContentProduct"),
									dialogHeader: t("educationalOffer.selectCourseContentProduct"),
									fieldLabel: t("educationalOffer.selectOneCourseContentProduct"),
								}}
							/>
						)}
						formikConfig={formik}
					/>
					<ValidatedField<EditSubscriberConfigFormState, Product>
						name="certificationProduct"
						label={t("offerSubscriptions.config.certificationProduct")}
						component={(props) => (
							<GenericSelectProductFieldDialog
								{...props}
								productType={{
									emptyText: t("educationalOffer.noCertificationProductSelected"),
									buttonLabel: t("educationalOffer.selectCertificationProduct"),
									dialogHeader: t("educationalOffer.selectCertificationProduct"),
									fieldLabel: t("educationalOffer.selectOneCertificationProduct"),
								}}
							/>
						)}
						formikConfig={formik}
					/>
				</>
			)}
		</form>
	);
});
